import { useEffect, useRef, useState, useCallback, type ReactElement } from 'react'
import { Volume2, VolumeX, SkipForward } from 'lucide-react'
import { AppShell } from '../components/AppShell.js'
import { useDemoStore } from '../state/store.js'
import { DEMO_CONTENT } from './demoContent.js'
import type { ScanConfig, AbortReason } from './types.js'

interface DemoVideoScanProps {
  scanConfig: ScanConfig
  onComplete: (findings: unknown) => void
  onAbort: (reason: AbortReason, detail?: string) => void
}

const STARTUP_WATCHDOG_MS = 10_000
const ERROR_PILL_DELAY_MS = 2000

// Instance counter so we can correlate logs across mount/unmount in DevTools.
let mountCounter = 0

/**
 * Desktop-only replacement for <LiveCameraScan>. Plays a per-scan recording
 * of the on-phone scan UI and, on `ended`, hydrates the store as if the real
 * scan had completed.
 *
 * Autoplay strategy (mirrors vue-rtc-web/VideoPlayer): <video autoPlay muted>
 * attribute starts itself; on canPlay we attempt an unmute since the
 * "Watch demo" click is fresh user activation; on rejection we fall back to
 * muted playback and surface a "Tap to unmute" pill. A startup watchdog
 * diverts to manual entry if `canplay` never fires (10 s). All lifecycle
 * events log to console under the `[demo]` prefix for in-browser debugging.
 */
export const DemoVideoScan = ({
  scanConfig,
  onComplete,
  onAbort,
}: DemoVideoScanProps): ReactElement => {
  const { dispatch } = useDemoStore()
  const videoRef = useRef<HTMLVideoElement>(null)
  const hasInitializedRef = useRef(false)
  const [muted, setMuted] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const errorPillTimerRef = useRef<number | null>(null)
  const watchdogRef = useRef<number | null>(null)
  const cancelledRef = useRef(false)
  const instanceRef = useRef<number>(0)
  if (instanceRef.current === 0) {
    mountCounter += 1
    instanceRef.current = mountCounter
  }
  const tag = `[demo:${scanConfig.id}#${instanceRef.current}]`

  const content = DEMO_CONTENT[scanConfig.id]

  // Lifecycle setup — watchdog + cancellation flag.
  useEffect(() => {
    console.log(`${tag} mount/effect — webm=${content.videoUrlWebm} mp4=${content.videoUrlMp4}`)
    cancelledRef.current = false
    watchdogRef.current = window.setTimeout(() => {
      if (cancelledRef.current) return
      console.warn(`${tag} startup watchdog fired for ${content.videoUrlMp4}`)
      setErrorMessage('Demo asset failed to load — entering manually')
      errorPillTimerRef.current = window.setTimeout(() => {
        if (cancelledRef.current) return
        console.warn(`${tag} onAbort('startup-watchdog')`)
        onAbort('session-drop', 'startup-watchdog')
      }, ERROR_PILL_DELAY_MS)
    }, STARTUP_WATCHDOG_MS)

    return () => {
      console.log(`${tag} unmount/cleanup`)
      cancelledRef.current = true
      if (watchdogRef.current !== null) {
        window.clearTimeout(watchdogRef.current)
        watchdogRef.current = null
      }
      if (errorPillTimerRef.current !== null) {
        window.clearTimeout(errorPillTimerRef.current)
        errorPillTimerRef.current = null
      }
      const video = videoRef.current
      if (video) {
        try { video.pause() } catch { /* ignore */ }
      }
    }
  }, [content.videoUrlMp4, onAbort, tag])

  const handleCanPlay = useCallback((): void => {
    if (hasInitializedRef.current) {
      console.log(`${tag} canplay (already initialized, skip)`)
      return
    }
    hasInitializedRef.current = true
    console.log(`${tag} canplay — attempting unmuted playback`)

    const video = videoRef.current
    if (!video) return

    void video.play().then(() => {
      if (cancelledRef.current) return
      video.muted = false
      void video.play().then(() => {
        if (cancelledRef.current) return
        console.log(`${tag} unmuted playback ✓`)
        setMuted(false)
      }).catch(err => {
        if (cancelledRef.current) return
        console.warn(`${tag} unmute rejected (${err instanceof Error ? err.name : err}) — falling back to muted`)
        video.muted = true
        void video.play().catch(() => {})
        setMuted(true)
      })
    }).catch(err => {
      console.warn(`${tag} play() rejected: ${err instanceof Error ? err.name : err}`)
    })
  }, [tag])

  const handlePlaying = useCallback((): void => {
    console.log(`${tag} playing (clearing startup watchdog)`)
    if (watchdogRef.current !== null) {
      window.clearTimeout(watchdogRef.current)
      watchdogRef.current = null
    }
  }, [tag])

  // Catch the "already canplay before listeners attached" case.
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    if (video.readyState >= 3 && !hasInitializedRef.current) {
      console.log(`${tag} catch-up: readyState=${video.readyState} on mount`)
      const id = requestAnimationFrame(() => {
        if (!hasInitializedRef.current) handleCanPlay()
      })
      return () => cancelAnimationFrame(id)
    }
  }, [handleCanPlay, tag])

  const advanceAsIfEnded = useCallback((reason: 'ended' | 'skipped'): void => {
    if (cancelledRef.current) return
    cancelledRef.current = true
    const v = videoRef.current
    console.log(`${tag} ${reason.toUpperCase()} at ct=${v?.currentTime.toFixed(2)} of ${v?.duration.toFixed(2)} — dispatching PICTURE_SAVED + onComplete(${scanConfig.id})`)
    try { v?.pause() } catch { /* ignore */ }
    if (watchdogRef.current !== null) {
      window.clearTimeout(watchdogRef.current)
      watchdogRef.current = null
    }
    dispatch({
      type: 'PICTURE_SAVED',
      picture: {
        scan: scanConfig.id,
        label: content.picture.label,
        dataUrl: content.picture.dataUrl,
        takenAt: Date.now(),
      },
    })
    onComplete(content.findings)
  }, [content.findings, content.picture.dataUrl, content.picture.label, dispatch, onComplete, scanConfig.id, tag])

  const handleEnded = (): void => advanceAsIfEnded('ended')
  const handleSkip = (): void => advanceAsIfEnded('skipped')

  const handleError = (): void => {
    const errCode = videoRef.current?.error?.code ?? 'unknown'
    console.warn(`${tag} video error for ${content.videoUrlMp4}: code=${errCode}`)
    setErrorMessage('Demo asset failed to load — entering manually')
    errorPillTimerRef.current = window.setTimeout(() => {
      if (cancelledRef.current) return
      onAbort('session-drop', `video-error-${errCode}`)
    }, ERROR_PILL_DELAY_MS)
  }

  const toggleMute = (): void => {
    const video = videoRef.current
    if (!video) return
    const next = !video.muted
    console.log(`${tag} toggleMute → muted=${next}`)
    video.muted = next
    setMuted(next)
    if (!next && video.paused) void video.play().catch(() => {})
  }

  return (
    <AppShell fullbleed>
      <div className="absolute inset-0 bg-background">
        <video
          ref={videoRef}
          poster={content.posterUrl}
          autoPlay
          muted={muted}
          playsInline
          preload="auto"
          onCanPlay={handleCanPlay}
          onPlaying={handlePlaying}
          onEnded={handleEnded}
          onError={handleError}
          className="absolute inset-0 w-full h-full object-contain"
        >
          {/* Chrome / Firefox prefer the smaller VP9; Safari falls back to mp4. */}
          <source src={content.videoUrlWebm} type="video/webm" />
          <source src={content.videoUrlMp4} type="video/mp4" />
        </video>

        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? 'Unmute' : 'Mute'}
          className="absolute top-4 right-4 z-10 grid place-items-center h-10 w-10 rounded-full bg-foreground/60 text-background hover:bg-foreground/80"
        >
          {muted
            ? <VolumeX className="h-5 w-5" />
            : <Volume2 className="h-5 w-5" />}
        </button>

        {muted && (
          <button
            type="button"
            onClick={toggleMute}
            className="absolute top-4 left-1/2 -translate-x-1/2 z-10 px-4 py-2 rounded-full bg-foreground/80 text-background text-sm font-medium"
          >
            Tap to unmute
          </button>
        )}

        {errorMessage !== null && (
          <div
            role="alert"
            className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 px-4 py-2 rounded-full bg-destructive text-destructive-foreground text-sm"
          >
            {errorMessage}
          </div>
        )}

        <button
          type="button"
          onClick={handleSkip}
          className="absolute bottom-[max(1.5rem,env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground/80 text-background text-sm font-medium hover:bg-foreground/90"
        >
          Skip replay
          <SkipForward className="h-4 w-4" />
        </button>
      </div>
    </AppShell>
  )
}
